import React, { useEffect, useMemo, useRef, FocusEvent } from "react";

import styled, { css } from "styled-components";

import { IInputHours, useInputHours } from "../../hooks/useInputHours";
import { formatHours, validateTimeInput } from "../../utils";
import useForkRef from "../../utils/useForkRef";
import { EditableContent, EditableContentInterface } from "../EditableContent";

export interface EditableHoursProps
  extends EditableContentInterface,
    Omit<IInputHours, "onChange"> {
  className?: string;
  ticking?: boolean;
  onFocus?: (event: FocusEvent) => void;
}

const StyledEditableContent = styled(EditableContent)<{ $ticking?: boolean }>`
  ${({ $ticking }) =>
    $ticking &&
    css`
      .presentation {
        visibility: visible;
      }

      input {
        opacity: 0;
      }
    `}
`;

export const EditableHours = React.forwardRef<
  HTMLDivElement,
  EditableHoursProps
>(
  (
    {
      value: defaultValue,
      onCancel,
      onSave,
      allowEmptyValue,
      withLeadingZero = true,
      validation = validateTimeInput,
      incrementOnlySelected = false,
      minuteIncrement = 1,
      className,
      onEnterKeyPress: externalEnterKeyPress,
      inputProps: externalInputProps,
      onClick: externalOnClick,
      ticking,
      onFocus,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef(ref, inputRef);

    const { inputProps, setCurrentValue, setPrevValue } = useInputHours(
      {
        value: defaultValue,
        withLeadingZero,
        onSave,
        validation,
        allowEmptyValue,
        onCancel,
        minuteIncrement,
        incrementOnlySelected,
        onEnterKeyPress: externalEnterKeyPress,
        onClick: externalOnClick,
      },
      inputRef
    );

    const { value, onBlur, onKeyDown, onChange, onClick, onDoubleClick } =
      inputProps;

    useEffect(() => {
      let _value;
      if (defaultValue !== undefined) {
        _value = formatHours(defaultValue, withLeadingZero);
      }
      setCurrentValue((prevState) => {
        if (prevState !== _value) {
          return _value;
        }
        return prevState;
      });
      setPrevValue((prevState) => {
        if (prevState !== _value) {
          return _value;
        }
        return prevState;
      });
    }, [defaultValue, setCurrentValue, setPrevValue, withLeadingZero]);

    const splitTime = useMemo(() => {
      if (value && ticking) {
        const [hours, minutes] = value.split(":");

        return {
          hours,
          minutes,
        };
      }
    }, [value, ticking]);

    return (
      <StyledEditableContent
        ref={handleRef}
        className={className}
        inputProps={{
          ...externalInputProps,
          value,
          onBlur,
          onKeyDown,
          onChange,
          onClick,
          onDoubleClick,
          onFocus,
          type: "text",
        }}
        $ticking={ticking}
        {...rest}
      >
        {ticking && (
          <>
            {splitTime?.hours}
            <span className="tw-animate-ping">:</span>
            {splitTime?.minutes}
          </>
        )}
      </StyledEditableContent>
    );
  }
);

EditableHours.displayName = "EditableHours";
